/* eslint-disable @typescript-eslint/no-misused-promises */
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  companyOpportunityInterviewSchema,
  CompanyOpportunityInterviewSchema,
} from "../opportunity/schema";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/ui/multi-select";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import PreprocessIcon from "@/assets/preprocess_icon.svg";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { useCompanyTeamQuery } from "@/queries/company/team";

import { CompanyInterview } from "@/types";
import useCompanyInterviewMutation from "./mutations";

interface Props {
  interview: CompanyInterview;
  jobOpeningId: string;
  companyId: string;
  onSuccess?: (data: CompanyInterview) => void;
}

const UpdateInterviewDialog = (props: Props) => {
  const [open, setOpen] = useState(false);

  const { companyTeam } = useCompanyTeamQuery(props.companyId);

  const onSuccess = (result: CompanyInterview) => {
    setOpen(false);
    props.onSuccess?.(result);
  };
  const {
    mutate,
    isPending: mutationLoading,
    isError: mutationError,
  } = useCompanyInterviewMutation(
    props.jobOpeningId,
    props.interview.id,
    onSuccess,
  );

  const form = useForm<CompanyOpportunityInterviewSchema>({
    resolver: async (data, context, opts) => {
      return zodResolver(companyOpportunityInterviewSchema)(
        data,
        context,
        opts,
      );
    },
    defaultValues: {
      id: props.interview?.id,
      name: props.interview?.name ?? "",
      duration: props.interview?.duration ?? 30,
      participants: props.interview?.participants ?? [],
    },
  });

  useEffect(() => {
    form.reset({
      id: props.interview?.id,
      name: props.interview?.name ?? "",
      duration: props.interview?.duration ?? 30,
      participants: props.interview?.participants ?? [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, props.interview]);

  const participantOptions = useMemo(() => {
    if (!companyTeam) return [];

    return companyTeam
      .map((member) => ({
        label: member.name ?? member.email ?? "",
        value: member.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [companyTeam]);

  function onSubmit(data: CompanyOpportunityInterviewSchema) {
    if (mutationLoading) return;
    mutate(data);
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary">Update Interview</Button>
      </DialogTrigger>
      <DialogContent className="w-full max-w-[min(calc(100vw-2rem),768px)] p-0">
        <DialogHeader className="p-6">
          <div className="flex items-center gap-2">
            <img src={PreprocessIcon} alt="Preprocess Icon" className="" />
            <DialogTitle>
              {props.interview ? "Update" : "Add"} an Interview
            </DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Enter the details of the interview here and click &quot;Save&quot;
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(100vh-10rem)] md:max-h-[70vh] px-6 pb-6">
          <Form {...form}>
            <div className="space-y-4 w-full">
              {mutationError && (
                <p className="text-center w-full border border-destructive rounded-md text-destructive p-2 bg-destructive/10">
                  Something went wrong when updating the interview, please try
                  again later.
                </p>
              )}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Interview Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="First Interview" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="duration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Duration (minutes)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        min={1}
                        step={1}
                        placeholder="30"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="participants"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Interviewer(s)</FormLabel>
                    <FormControl>
                      <MultiSelect
                        values={field.value}
                        setValues={field.onChange}
                        labeledOptions={participantOptions}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div
                className={`flex ${props.interview ? "justify-between" : "justify-end"}`}
              >
                <Button
                  type="button"
                  className="mt-2"
                  onClick={form.handleSubmit(onSubmit)}
                >
                  Save
                </Button>
              </div>
            </div>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateInterviewDialog;
